import type { MoneyValue } from '@akademi/site-builder-runtime';

/**
 * Decimal-safe money formatting for {@link MoneyValue}.
 *
 * <p><strong>Why this file exists.</strong> `MoneyValue.amount` is a decimal
 * string on purpose — the backend went to some trouble to keep it exact.
 * `Number(amount)` (or `parseFloat`) reintroduces binary floating point on
 * that value: `£0.1 + £0.2 !== £0.3`, and a catalogue that is a penny out
 * on a £4,000 kit is a catalogue nobody trusts. Every function below does
 * its arithmetic in {@link BigInt} minor-unit space and only ever touches
 * `Intl.NumberFormat` to borrow a locale's grouping/decimal separators and
 * currency-symbol placement — never to parse or round the real amount.
 *
 * <p>Locale is always a caller-supplied argument (from
 * {@code useLocale()`s `locale`, e.g. `'en'` / `'tr'`}) — this file never
 * defaults to `en-GB` or any other locale, matching the platform running in
 * more than one country.
 */

interface ParsedDecimal {
  /** `'-'` or `''`. */
  sign: string;
  /** Digits only, integer part followed by fraction part (no separators). */
  digits: string;
  /** How many of `digits`' trailing characters are the fraction part. */
  scale: number;
}

const DECIMAL_RE = /^\s*(-)?(\d+)(?:\.(\d+))?\s*$/;

function parseDecimal(raw: string): ParsedDecimal | null {
  const m = DECIMAL_RE.exec(raw);
  if (!m) return null;
  const sign = m[1] ?? '';
  const intPart = m[2] ?? '0';
  const fracPart = m[3] ?? '';
  return { sign, digits: intPart + fracPart, scale: fracPart.length };
}

/**
 * Round `digits` (an unsigned integer string at `fromScale` decimal places)
 * to `toScale` decimal places, round-half-up, using exact integer
 * arithmetic — never a float. Returns a digit string at least `toScale + 1`
 * characters long (so the caller can always slice off the last `toScale`
 * as the fraction and keep at least one integer digit).
 */
function rescale(digits: string, fromScale: number, toScale: number): string {
  // `BigInt(10)` rather than the `10n` literal, and likewise `BigInt(2)`.
  // This package inherits `target: ES2022` from the workspace base, where a
  // BigInt literal is legal — but `apps/lms/portal` sets its own
  // `target: ES2017` and compiles this source directly, where `10n` is a
  // TS2737 error and broke that app's build. Its `lib` includes `esnext`, so
  // the BigInt *global* is available; only the literal syntax is not. A
  // shared package should compile under its consumers' targets rather than
  // oblige every consumer to raise theirs.
  const TEN = BigInt(10);
  const TWO = BigInt(2);
  let value = BigInt(digits || '0');
  if (toScale > fromScale) {
    value *= TEN ** BigInt(toScale - fromScale);
  } else if (toScale < fromScale) {
    const divisor = TEN ** BigInt(fromScale - toScale);
    value = (value + divisor / TWO) / divisor;
  }
  return value.toString().padStart(toScale + 1, '0');
}

/** Group an unsigned integer digit string into 3-digit chunks with `sep`.
 *  Western/Turkish-style grouping only — the two locales this platform
 *  actually ships (`en`, `tr`) both group in 3s. A locale with irregular
 *  grouping (e.g. `hi-IN`'s lakh/crore) would need a real grouping-size
 *  table here; out of scope while the platform is UK/Turkey-only. */
function groupDigits(digits: string, sep: string): string {
  if (!sep || digits.length <= 3) return digits;
  const chunks: string[] = [];
  for (let end = digits.length; end > 0; end -= 3) {
    chunks.unshift(digits.slice(Math.max(0, end - 3), end));
  }
  return chunks.join(sep);
}

/**
 * Format a {@link MoneyValue} for display, in `locale`, without ever
 * converting `amount` to a JS number. Never throws — an unparsable amount
 * or an unknown currency code falls back to `"<amount> <currency>"` so a
 * bad backend value degrades the row, not the page.
 */
export function formatMoney(value: MoneyValue, locale: string): string {
  const parsed = parseDecimal(value.amount);
  if (!parsed) return `${value.amount} ${value.currency}`.trim();

  let nf: Intl.NumberFormat;
  try {
    nf = new Intl.NumberFormat(locale || undefined, {
      style: 'currency',
      currency: value.currency,
    });
  } catch {
    return `${value.amount} ${value.currency}`.trim();
  }

  const { maximumFractionDigits } = nf.resolvedOptions();
  const targetScale = maximumFractionDigits ?? 2;
  const rescaled = rescale(parsed.digits, parsed.scale, targetScale);
  const fracDigits = targetScale > 0 ? rescaled.slice(-targetScale) : '';
  const intDigits = targetScale > 0 ? rescaled.slice(0, -targetScale) || '0' : rescaled;
  // Avoid a "-£0.00" for an amount that rounds down to exactly zero.
  const isNegative = parsed.sign === '-' && /[1-9]/.test(rescaled);

  // Format a throwaway value of the same sign to learn this locale's
  // separator characters + symbol placement — the actual digits below come
  // entirely from `intDigits` / `fracDigits`, never from this call.
  const templateParts = nf.formatToParts(isNegative ? -1234 : 1234);
  let groupSep = '';
  let decimalSep = '.';
  for (const part of templateParts) {
    if (part.type === 'group') groupSep = part.value;
    if (part.type === 'decimal') decimalSep = part.value;
  }
  const groupedInt = groupDigits(intDigits, groupSep);

  let out = '';
  let integerEmitted = false;
  for (const part of templateParts) {
    switch (part.type) {
      case 'integer':
      case 'group':
        if (!integerEmitted) {
          out += groupedInt;
          integerEmitted = true;
        }
        break;
      case 'decimal':
        if (fracDigits.length > 0) out += decimalSep;
        break;
      case 'fraction':
        out += fracDigits;
        break;
      default:
        out += part.value;
    }
  }
  return out;
}

/**
 * `formatMoney`, but returns `undefined` for an absent value instead of
 * formatting — the common case at a call site rendering an optional
 * `listPrice` / `price`.
 */
export function formatMoneyOrUndefined(
  value: MoneyValue | undefined,
  locale: string,
): string | undefined {
  return value ? formatMoney(value, locale) : undefined;
}

/**
 * `formatMoney` for the cart and order shapes, where the amount and the
 * currency arrive separately.
 *
 * <p>A basket states its currency once and every figure in it is a bare decimal
 * string, so there is no {@link MoneyValue} to hand over. This adapts to the
 * same formatter rather than duplicating it — in particular it still never
 * converts the amount to a JS number.
 *
 * <p>`taxInclusive` is passed as false because nothing here renders it: a cart
 * shows tax on its own line, so labelling each figure "inc. VAT" as well would
 * say the same thing twice and, on the subtotal, say it wrongly.
 */
export function formatDecimalMoney(
  amount: string | undefined | null,
  currency: string,
  locale: string,
): string {
  if (amount == null || amount === '') return '';
  return formatMoney({ amount, currency, taxInclusive: false }, locale);
}
